import { DateTime } from "luxon";
import { useMemo } from "react";

import { EmptyState } from "@/components/common/EmptyState";
import { RowActions } from "@/components/common/RowActions";
import type { Vending } from "@/features/vending/api/vendingApi";
import { formatMoney } from "@/lib/format";

type Sale = {
  key: string;
  label: string;
  at: string;
  lines: Vending[];
  total: number;
};

/**
 * The API stores one record per product sold, with no sale aggregate, so the
 * screen regroups them by label and timestamp to show what was one basket.
 */
function groupSales(vendings: Vending[]): Sale[] {
  const groups = new Map<string, Vending[]>();

  for (const vending of vendings) {
    const minute = vending.vendedAt?.slice(0, 16) ?? vending.createdAt.slice(0, 16);
    const key = `${vending.label ?? ""}@${minute}`;
    groups.set(key, [...(groups.get(key) ?? []), vending]);
  }

  return Array.from(groups.entries())
    .map(([key, lines]) => ({
      key,
      label: lines[0]?.label || "Vente",
      at: lines[0]?.vendedAt ?? lines[0]?.createdAt ?? "",
      lines,
      total: lines.reduce((sum, line) => sum + line.price * line.quantity, 0),
    }))
    .sort((a, b) => b.at.localeCompare(a.at));
}

export function SalesHistory({
  vendings,
  currency,
  onEdit,
  onDelete,
}: {
  vendings: Vending[];
  currency: string;
  onEdit: (vending: Vending) => void;
  onDelete: (vending: Vending) => void;
}) {
  const sales = useMemo(() => groupSales(vendings), [vendings]);

  const revenue = useMemo(() => sales.reduce((sum, sale) => sum + sale.total, 0), [sales]);

  if (sales.length === 0) {
    return <EmptyState title="Aucune vente" hint="Les ventes encaissées apparaîtront ici." />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[15px] text-muted-foreground">
        Recette <span className="font-semibold tabular-nums text-foreground">{formatMoney(revenue, currency)}</span>
        {" · "}
        {sales.length} vente{sales.length > 1 ? "s" : ""}
      </p>

      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {sales.map((sale) => (
          <li key={sale.key} className="border-b border-muted last:border-b-0">
            <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/50 px-4 py-3">
              <span className="flex items-center gap-3">
                <span className="font-semibold tabular-nums">
                  {sale.at ? DateTime.fromISO(sale.at).toFormat("HH:mm") : "—"}
                </span>
                <span className="text-[15px]">{sale.label}</span>
              </span>
              <span className="font-semibold tabular-nums">{formatMoney(sale.total, currency)}</span>
            </div>

            <ul>
              {sale.lines.map((line) => (
                <li key={line.id} className="flex items-center gap-3 px-4 py-2 pl-8 text-sm">
                  <span className="min-w-0 flex-1 truncate">{line.productName}</span>
                  <span className="text-muted-foreground tabular-nums">×{line.quantity}</span>
                  <span className="w-24 text-right tabular-nums">
                    {formatMoney(line.price * line.quantity, currency)}
                  </span>
                  <RowActions
                    onEdit={() => onEdit(line)}
                    onDelete={() => onDelete(line)}
                    editLabel="Corriger la vente"
                    deleteLabel="Annuler la vente"
                  />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
